/**
 * SignPage.js
 *
 * Public-facing page — no login required.
 * Recipients arrive via email link: /sign/:token
 * They view the PDF, draw/type their signature, and submit.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

export default function SignPage() {
  const { token } = useParams();
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [data, setData]         = useState(null);
  const [sigMode, setSigMode]   = useState('draw');  // 'draw' | 'type'
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);
  const [declined, setDeclined] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawn = useRef(false);

  // Load signing data
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/sign/${token}`);
        const json = await res.json();
        if (!res.ok) { setError(json.error || 'Link not found'); return; }
        if (json.already_signed) { setDone(true); setData(json); return; }
        if (json.declined) { setDeclined(true); setData(json); return; }
        setData(json);
      } catch { setError('Failed to load. Please try again.'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  // Canvas drawing
  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#0c1628';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches ? e.touches[0] : e;
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const start = (e) => { e.preventDefault(); drawingRef.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); hasDrawn.current = true; const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
  };

  const handleSubmit = async () => {
    if (sigMode === 'draw' && !hasDrawn.current) { alert('Please draw your signature above.'); return; }
    if (sigMode === 'type' && !typedName.trim()) { alert('Please type your name.'); return; }

    setSubmitting(true);
    try {
      const body = {
        signature_type: sigMode,
        action: 'sign',
      };
      if (sigMode === 'draw') {
        body.signature_data = canvasRef.current.toDataURL('image/png');
      } else {
        body.typed_name = typedName.trim();
        // Generate a typed signature image on a small canvas
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = 400; tmpCanvas.height = 100;
        const tmpCtx = tmpCanvas.getContext('2d');
        tmpCtx.fillStyle = '#ffffff';
        tmpCtx.fillRect(0, 0, 400, 100);
        tmpCtx.fillStyle = '#0c1628';
        tmpCtx.font = 'italic 32px "Dancing Script", "Brush Script MT", cursive';
        tmpCtx.fillText(typedName.trim(), 20, 60);
        body.signature_data = tmpCanvas.toDataURL('image/png');
      }

      const res = await fetch(`${API}/api/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) { alert(json.error || 'Failed to submit'); return; }
      setDone(true);
    } catch { alert('Failed to submit signature. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline to sign this document?')) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/public/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'decline' }),
      });
      setDeclined(true);
    } catch { alert('Failed to decline.'); }
    finally { setSubmitting(false); }
  };

  // ── Render states ──
  if (loading) return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={{ color: '#7a96b8', marginTop: 16 }}>Loading document...</p>
    </div>
  );

  if (error) return (
    <div style={styles.center}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>!</div>
      <h2 style={{ color: '#0e1f38', marginBottom: 8 }}>Unable to Load</h2>
      <p style={{ color: '#7a96b8' }}>{error}</p>
    </div>
  );

  if (declined) return (
    <div style={styles.center}>
      <h2 style={{ color: '#0e1f38', marginBottom: 8 }}>Document Declined</h2>
      <p style={{ color: '#7a96b8' }}>You have declined to sign "{data?.document_title}".</p>
      <p style={{ color: '#7a96b8', fontSize: '0.85rem' }}>The requesting party has been notified.</p>
    </div>
  );

  if (done) return (
    <div style={styles.center}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h2 style={{ color: '#0e1f38', marginBottom: 8 }}>Signature Submitted</h2>
      <p style={{ color: '#7a96b8' }}>Thank you, {data?.recipient_name || 'your signature has been recorded'}.</p>
      <p style={{ color: '#7a96b8', fontSize: '0.85rem' }}>You may close this window.</p>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.brand}>Zeus<span style={{ color: '#d4a853' }}>CMS</span></div>
        <div style={{ fontSize: '0.72rem', color: '#7a96b8' }}>DocuSeal</div>
      </div>

      <div style={styles.container}>
        {/* Document info */}
        <div style={styles.docCard}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7a96b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Document</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0e1f38', marginBottom: 4 }}>{data.document_title}</div>
          <div style={{ fontSize: '0.82rem', color: '#7a96b8' }}>From: {data.firm_name}</div>
          <div style={{ fontSize: '0.82rem', color: '#7a96b8', marginTop: 2 }}>To: {data.recipient_name || data.recipient_email}</div>
        </div>

        {/* PDF Preview */}
        {data.pdf_url && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7a96b8', marginBottom: 6 }}>Review the document below before signing:</div>
            <iframe
              src={data.pdf_url}
              title="Document Preview"
              style={{ width: '100%', height: '50vh', border: '1px solid #d0daea', borderRadius: 8, background: '#fff' }}
            />
          </div>
        )}

        {/* Signature section */}
        <div style={styles.sigCard}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#7a96b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Your Signature</div>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button onClick={() => setSigMode('draw')} style={{ ...styles.modeBtn, ...(sigMode === 'draw' ? styles.modeBtnActive : {}) }}>Draw</button>
            <button onClick={() => setSigMode('type')} style={{ ...styles.modeBtn, ...(sigMode === 'type' ? styles.modeBtnActive : {}) }}>Type</button>
          </div>

          {sigMode === 'draw' ? (
            <div>
              <canvas
                ref={initCanvas}
                width={500}
                height={150}
                style={{ width: '100%', height: 150, border: '2px dashed #d0daea', borderRadius: 8, cursor: 'crosshair', background: '#fafcff', touchAction: 'none' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                <button onClick={clearCanvas} style={{ background: 'none', border: 'none', color: '#7a96b8', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
              </div>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder="Type your full name"
                style={styles.input}
              />
              {typedName && (
                <div style={{ marginTop: 10, padding: '12px 20px', background: '#fafcff', border: '1px solid #d0daea', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.68rem', color: '#7a96b8', marginBottom: 4 }}>Preview:</div>
                  <div style={{ fontFamily: '"Brush Script MT", "Dancing Script", cursive', fontSize: '1.8rem', color: '#0e1f38' }}>{typedName}</div>
                </div>
              )}
            </div>
          )}

          {/* Legal text */}
          <p style={{ fontSize: '0.75rem', color: '#7a96b8', lineHeight: 1.6, marginTop: 14, marginBottom: 14 }}>
            By clicking "Sign Document" below, I agree that my electronic signature is the legal equivalent of my handwritten signature.
            I consent to be legally bound by this document.
            This transaction is conducted under the ESIGN Act and UETA.
          </p>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSubmit} disabled={submitting} style={styles.signBtn}>
              {submitting ? 'Submitting...' : 'Sign Document'}
            </button>
            <button onClick={handleDecline} disabled={submitting} style={styles.declineBtn}>
              Decline
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        Powered by DocuSeal | Electronic signatures are legally binding under the ESIGN Act (15 U.S.C. 7001) and UETA.
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f4fa', display: 'flex', flexDirection: 'column' },
  header: { background: '#0c1628', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #d4a853' },
  brand: { fontSize: '1rem', fontWeight: 700, color: '#e8edf5', letterSpacing: '0.03em' },
  container: { maxWidth: 700, width: '100%', margin: '24px auto', padding: '0 16px', flex: 1 },
  docCard: { background: '#fff', border: '1px solid #d0daea', borderRadius: 10, padding: '20px 24px', marginBottom: 20 },
  sigCard: { background: '#fff', border: '1px solid #d0daea', borderRadius: 10, padding: '20px 24px', marginBottom: 20 },
  modeBtn: { padding: '7px 18px', borderRadius: 6, border: '1px solid #d0daea', background: '#f8faff', color: '#3a5272', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  modeBtnActive: { background: '#0c1628', color: '#d4a853', borderColor: '#0c1628' },
  input: { width: '100%', padding: '12px 16px', fontSize: '1rem', border: '1px solid #d0daea', borderRadius: 8, outline: 'none', color: '#0e1f38', fontFamily: 'inherit', boxSizing: 'border-box' },
  signBtn: { flex: 1, padding: '14px 24px', background: '#d4a853', color: '#0c1628', fontWeight: 700, fontSize: '0.9rem', border: 'none', borderRadius: 8, cursor: 'pointer' },
  declineBtn: { padding: '14px 20px', background: 'transparent', color: '#7a96b8', fontWeight: 600, fontSize: '0.82rem', border: '1px solid #d0daea', borderRadius: 8, cursor: 'pointer' },
  footer: { textAlign: 'center', padding: '16px', fontSize: '0.68rem', color: '#7a96b8' },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', background: '#f0f4fa' },
  spinner: { width: 32, height: 32, border: '3px solid #d0daea', borderTopColor: '#d4a853', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },
};
